import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// Stripe
import { getStripe } from '../../aux/stripeHelpers';
import { Elements } from '@stripe/react-stripe-js';
import { Grid, Typography, Box, Divider, Button } from '@material-ui/core';
import CheckoutPaymentMethod from '../CheckoutPaymentMethod/CheckoutPaymentMethod';
import CheckoutPaymentMethodSkeleton from '../CheckoutPaymentMethodSkeleton/CheckoutPaymentMethodSkeleton';
import { requestMinimumTimeoutWrapper } from '../../aux/requestMethods';
import { createPaymentIntentFn } from '../../aux/purchaseQueries';
import { isEmptyObject, isNotEmptyObject } from '../../aux/aux';
// SVg & img
import StripeLogo from '../../img/stripe-logo.svg'
import './checkoutPay.css';
import { useTheme } from '@material-ui/styles';
import CheckoutPromoCode from '../CheckoutPromoCode/CheckoutPromoCode';
import { rgbToHex } from '@material-ui/core';
import { getReliableLocale } from '../../aux/sessionHelpers';
import BackButton from '../BackButton/BackButton';
import CheckoutTotal from '../CheckoutTotal/CheckoutTotal';
import { getUserContactData, getUserDefaultPaymentMethod } from '../../services/userServices';

const CheckoutPay = ({ 
    appliedPromoCode, checkoutContent,
    handlerIsCheckoutPaymentMethodReady, handlerPaymentIntentClientSecret, addMessages, setPaymentIntentId, checkoutSyncAfterPIFailureCallback,
    updateCheckoutStatus, updatePaymentIntent
}) => {
    const theme = useTheme();
    const location = useLocation();
    const { t } = useTranslation('common', { keyPrefix: 'fastCheckoutPay' });
    const locale = getReliableLocale();
    const [isLoading, setIsLoading] = useState(false);
    const [paymentIntentClientSecret, setPaymentIntentClientSecret] = handlerPaymentIntentClientSecret;
    // const [purchaseProcess, setPurchaseProcess] = handlerPurchaseStep;
    const [isCheckoutPaymentMethodReady, setIsCheckoutPaymentMethodReady] = handlerIsCheckoutPaymentMethodReady;
    const [redirectUrl, setRedirectUrl] = useState(null); 
    const [defaultPaymentMethod, setDefaultPaymentMethod] = useState(null);
    const [userContactData, setUserContactData] = useState(null);
    const abortControllerSignalRef = useRef(null);
    const stripeCheckoutAppearance = {
        theme:'stripe',
        variables:{
            fontFamily: theme.typography.body1.fontFamily,
            fontSizeBase: theme.typography.fontSize,
            colorText: rgbToHex(theme.palette.text.primary),
            colorPrimary: theme.palette.primary.main,
        }
    };
    const stripeCheckoutOptions = {
        appearance: stripeCheckoutAppearance,
        clientSecret: paymentIntentClientSecret,
    }

    // const handleOnBackButtonClick = (e) => {
    //     e.preventDefault();
    //     setPurchaseProcess(purchaseProcess => purchaseProcess - 1);
    //     setIsCheckoutPaymentMethodReady(false);
    // }

    const getPaymentIntentClientSecret = async (userId, checkoutContent, userLocaleCode, signal) => {
        try{
            const requestParams = {
                userId,
                checkoutId: checkoutContent.id,
                userLocaleCode,
                paymentMethodUsage : isEmptyObject(checkoutContent.subscriptionProductMap) ? 'on_session' : 'off_session',
                hashFinalAmount: checkoutContent.orderAmounts.hashFinalAmount,
                currencyId: checkoutContent.currencyId,
            };
            const res = await requestMinimumTimeoutWrapper(createPaymentIntentFn, requestParams, 0, signal);
            // const redirectUrl = window.location.origin + location.pathname;
            // setRedirectUrl(redirectUrl);
            if(!signal || !signal.aborted){
                setPaymentIntentClientSecret(res.data.paymentIntentClientSecret);
            }
        }catch(error){
            // TODO: implement;
        };
    };

    const requestDefaultPaymentMethod = async (userId, signal) => {
        let result = {};
        try{
            const res = await getUserDefaultPaymentMethod(userId, 0, signal);
            result = res.data;
        }catch(error){
            // TODO: implement
            console.log('Default payment method no available');
        }finally{
            if(!signal.aborted){
                setDefaultPaymentMethod(result);
            }
        }
    }

    const requestUserContactData = async(userId, signal) => {
        let result = {};
        try{
            const res = await getUserContactData(userId, 0, signal);
            result = res.data;
        }catch(error){
            // Contact data is collected in the payment element
        }finally{
            if(!signal.aborted){
                setUserContactData(result);
            }
        }
    }

    const updatePaymentIntentWithPaymentMethod = async(paymentMethodId) => {
        const updateIntentParams = {
            paymentMethodId,
            userId: checkoutContent.userId, 
            checkoutId: checkoutContent.id,
            paymentIntentClientSecret,
        }
        return updatePaymentIntent(updateIntentParams, 0)
    }

    useEffect(() => {
        if(isNotEmptyObject(checkoutContent)){
            const abortController = new AbortController();
            abortControllerSignalRef.current = abortController.signal;
            setRedirectUrl(window.location.origin + location.pathname + `?fast_checkout_id=${checkoutContent.id}`)
            requestDefaultPaymentMethod(checkoutContent.userId, abortController.signal);
            requestUserContactData(checkoutContent.userId, abortController.signal);
            return (() => abortController.abort())
        }
    },[checkoutContent])

    useEffect(() => {
        if(!paymentIntentClientSecret && isNotEmptyObject(checkoutContent)){
            const abortController = new AbortController();
            getPaymentIntentClientSecret(checkoutContent.userId, checkoutContent, locale.localeCode, abortController.signal);
            return (() => abortController.abort());
        }
    },[paymentIntentClientSecret, checkoutContent])

    return (
        <Grid container direction='column' >
             {/* <Grid item>
                <Grid container justifyContent='space-between'>
                    <Grid item>
                        <Typography variant="h5">
                            <Box fontWeight="bold">
                            {t('payThePurchase')}
                            </Box>
                        </Typography>
                        <Typography variant='body2'>
                            {t("lastStepBeforeEnjoying")}
                        </Typography>
                    </Grid>
                </Grid>
            </Grid>
            <Divider style={{marginTop:"10px"}}/>
            <Grid item>
                <BackButton isDisabled={isLoading} onClick={handleOnBackButtonClick}/>
            </Grid> */}
            <Grid item className='checkout-pay-total'>
                <CheckoutTotal isCheckoutUpdating={isLoading} orderAmounts={checkoutContent?.orderAmounts} appliedPromoCode={appliedPromoCode} />
            </Grid>
            <Grid item className='checkout-pay-payment-method'>
                {paymentIntentClientSecret && redirectUrl && defaultPaymentMethod && userContactData &&
                    <Elements options={stripeCheckoutOptions} stripe={getStripe()} >
                        <CheckoutPaymentMethod 
                            checkoutId={checkoutContent.id}
                            checkoutProductsMap={checkoutContent.productsAndCouponsMap}
                            addMessages={addMessages} 
                            billingAddress={checkoutContent}
                            contactData={userContactData}
                            redirectUrl={redirectUrl} 
                            setPaymentIntentId={setPaymentIntentId} 
                            setIsReady={setIsCheckoutPaymentMethodReady} 
                            isReady={isCheckoutPaymentMethodReady} 
                            defaultPaymentMethod={isEmptyObject(defaultPaymentMethod) ? null : defaultPaymentMethod}
                            finalAmountObject={ {hashFinalAmount: checkoutContent.orderAmounts.hashFinalAmount, currencyId: checkoutContent.currencyId} } 
                            handleIsCheckoutUpdating={[isLoading, setIsLoading]} 
                            updateCheckoutStatus={updateCheckoutStatus}
                            checkoutSyncAfterPIFailureCallback={checkoutSyncAfterPIFailureCallback} 
                            updatePaymentIntentWithPaymentMethod={updatePaymentIntentWithPaymentMethod}
                        />
                    </Elements>
                }
                {!isCheckoutPaymentMethodReady &&
                    <CheckoutPaymentMethodSkeleton />
                }
            </Grid>
            <Grid item className="checkout-pay-stripe-logo" align="right">
                <StripeLogo height={20} />
            </Grid>
        </Grid>
    )
}

export default CheckoutPay